import * as React from 'react';
import { Select, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import {
  TimeBoundary,
  ConversionUnit,
  TimeBoundaryType as TimeBoundaryTypeEnum
} from '@/gql/graphql';
import { DatePicker } from '@/components/date-picker';
import { DateRange } from 'react-day-picker';
import { formatISO, parseISO, isValid } from 'date-fns';
import { StyledSelectTrigger } from './StyledSelectTrigger';

interface TimeBoundarySelectorProps {
  value: TimeBoundary | null | undefined;
  onChange: (newTimeBoundary: TimeBoundary) => void;
}

const safeParseISO = (isoString: string | undefined | null): Date | undefined => {
  if (!isoString) return undefined;
  try {
    const date = parseISO(isoString);
    return isValid(date) ? date : undefined;
  } catch {
    return undefined;
  }
};

export function TimeBoundarySelector({ value, onChange }: TimeBoundarySelectorProps) {
  const currentType = value?.type ?? TimeBoundaryTypeEnum.AnyTime;
  const currentNumUnit = value?.conversionWindow?.num_unit ?? 30;
  const currentUnit = value?.conversionWindow?.conversion_unit ?? ConversionUnit.Day;

  const [dateRange, setDateRange] = React.useState<DateRange | undefined>(() => ({
    from: safeParseISO(value?.timeRange?.fromTime),
    to: safeParseISO(value?.timeRange?.toTime)
  }));

  React.useEffect(() => {
    setDateRange({
      from: safeParseISO(value?.timeRange?.fromTime),
      to: safeParseISO(value?.timeRange?.toTime)
    });
  }, [value?.timeRange?.fromTime, value?.timeRange?.toTime]);

  const handleTypeChange = (newTypeValue: string) => {
    const newType = newTypeValue as TimeBoundaryTypeEnum;
    let newBoundary: TimeBoundary;

    switch (newType) {
      case TimeBoundaryTypeEnum.ConversionWindow:
        newBoundary = {
          type: TimeBoundaryTypeEnum.ConversionWindow,
          conversionWindow: { num_unit: 30, conversion_unit: ConversionUnit.Day },
          timeRange: null
        };
        setDateRange({ from: undefined, to: undefined });
        break;
      case TimeBoundaryTypeEnum.TimeRange:
        const defaultFrom = dateRange?.from ?? new Date();
        const defaultTo = dateRange?.to ?? new Date();
        setDateRange({ from: defaultFrom, to: defaultTo });
        newBoundary = {
          type: TimeBoundaryTypeEnum.TimeRange,
          timeRange: {
            fromTime: formatISO(defaultFrom),
            toTime: formatISO(defaultTo)
          },
          conversionWindow: null
        };
        break;
      case TimeBoundaryTypeEnum.AnyTime:
      default:
        newBoundary = {
          type: TimeBoundaryTypeEnum.AnyTime,
          conversionWindow: null,
          timeRange: null
        };
        setDateRange({ from: undefined, to: undefined });
        break;
    }
    onChange(newBoundary);
  };

  const handleNumUnitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const num = parseInt(event.target.value, 10);
    if (!isNaN(num) && num >= 0) {
      onChange({
        type: TimeBoundaryTypeEnum.ConversionWindow,
        conversionWindow: {
          num_unit: num,
          conversion_unit: currentUnit
        },
        timeRange: null
      });
    }
  };

  const handleUnitChange = (newUnitValue: string) => {
    const unit = newUnitValue as ConversionUnit;
    if (Object.values(ConversionUnit).includes(unit)) {
      onChange({
        type: TimeBoundaryTypeEnum.ConversionWindow,
        conversionWindow: {
          num_unit: currentNumUnit,
          conversion_unit: unit
        },
        timeRange: null
      });
    }
  };

  const handleDateRangeChange = (newDateRange: DateRange | undefined) => {
    setDateRange(newDateRange);

    if (newDateRange?.from && newDateRange.to) {
      const newBoundary: TimeBoundary = {
        type: TimeBoundaryTypeEnum.TimeRange,
        timeRange: {
          fromTime: formatISO(newDateRange.from),
          toTime: formatISO(newDateRange.to)
        },
        conversionWindow: null
      };
      onChange(newBoundary);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Select value={currentType} onValueChange={handleTypeChange}>
        <StyledSelectTrigger>
          <SelectValue placeholder="Time Frame" />
        </StyledSelectTrigger>
        <SelectContent>
          <SelectItem value={TimeBoundaryTypeEnum.AnyTime}>any time</SelectItem>
          <SelectItem value={TimeBoundaryTypeEnum.ConversionWindow}>in the last</SelectItem>
          <SelectItem value={TimeBoundaryTypeEnum.TimeRange}>between</SelectItem>
        </SelectContent>
      </Select>

      {currentType === TimeBoundaryTypeEnum.ConversionWindow && (
        <React.Fragment>
          <Input
            type="number"
            className="w-[60px] h-7 text-sm bg-tertiary-sky text-tertiary-sky-foreground border-0 px-2 rounded-md focus-visible:ring-offset-0 focus-visible:ring-1 focus-visible:ring-ring"
            value={currentNumUnit}
            onChange={handleNumUnitChange}
            min={0}
          />
          <Select value={currentUnit} onValueChange={handleUnitChange}>
            <StyledSelectTrigger>
              <SelectValue placeholder="Unit" />
            </StyledSelectTrigger>
            <SelectContent>
              {Object.values(ConversionUnit).map((val) => (
                <SelectItem key={val} value={val}>
                  {val.toLowerCase().replace('_', ' ')}(s)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </React.Fragment>
      )}

      {currentType === TimeBoundaryTypeEnum.TimeRange && (
        <DatePicker value={dateRange} onChange={handleDateRangeChange} />
      )}
    </div>
  );
}
